/**
 * Status filter menu: radios All / Active / Inactive, All statuses checkbox,
 * grouped Active then Inactive lists. Not a ToolbarIconDropdown (those menus
 * close on item click; Email/Export must keep that).
 *
 * Portal z-index 10000, Escape, click-outside — same as ToolbarIconDropdown.
 * Wired on operational Target Investors and Client View `/view/targets`.
 * Syndication keeps ToolbarIconDropdown.
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckIcon, MinusIcon } from '@heroicons/react/24/solid';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import {
  toolbarDropdownPanelPortaled,
  toolbarStandaloneActive,
  toolbarStandaloneBase,
  toolbarStandaloneInactive,
} from './listToolbarTokens';
import {
  ACTIVE_STATUS_OPTIONS,
  INACTIVE_STATUS_OPTIONS,
  getAllStatusesCheckState,
  getStatusFilterTriggerLabel,
  isStatusFilterActive,
  selectStatusFilterRadio,
  toggleAllStatuses,
  toggleStatusFilterValue,
  type AllStatusesCheckState,
  type TargetInvestorStatusFilterRadio,
  type TargetInvestorStatusFilterState,
} from '../../utils/targetInvestorStatusFilter';

export type TargetInvestorStatusFilterProps = {
  'aria-label'?: string;
  value: TargetInvestorStatusFilterState;
  onChange: (next: TargetInvestorStatusFilterState) => void;
  disabled?: boolean;
  menuAlign?: 'left' | 'right';
  className?: string;
  triggerClassName?: string;
  title?: string;
};

const MENU_MARGIN = 8;
const MENU_GAP = 4;
const MENU_FALLBACK_WIDTH = 288;
const RADIOS: { id: TargetInvestorStatusFilterRadio; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'inactive', label: 'Inactive' },
];

const checkBoxClass = (state: AllStatusesCheckState | 'checked' | 'unchecked') =>
  `flex h-4 w-4 shrink-0 items-center justify-center rounded ${
    state === 'unchecked'
      ? 'border border-primary-500/30 bg-white'
      : 'bg-primary-500 text-white'
  }`;

const StatusCheckMark: React.FC<{ state: AllStatusesCheckState | 'checked' | 'unchecked' }> = ({
  state,
}) => (
  <span aria-hidden className={checkBoxClass(state)}>
    {state === 'checked' ? <CheckIcon className="h-3 w-3" /> : null}
    {state === 'indeterminate' ? <MinusIcon className="h-3 w-3" /> : null}
  </span>
);

const TargetInvestorStatusFilter: React.FC<TargetInvestorStatusFilterProps> = ({
  'aria-label': ariaLabel = 'Filter by progress',
  value,
  onChange,
  disabled = false,
  menuAlign = 'left',
  className = '',
  triggerClassName = '',
  title,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const radioRefs = useRef<
    Record<TargetInvestorStatusFilterRadio, HTMLButtonElement | null>
  >({
    all: null,
    active: null,
    inactive: null,
  });
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);

  const isActive = isStatusFilterActive(value);
  const triggerLabel = getStatusFilterTriggerLabel(value);
  const allCheck = getAllStatusesCheckState(value);
  const selected = new Set(value.selectedKeys);

  const placeMenu = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const panelWidth = menuRef.current?.offsetWidth || MENU_FALLBACK_WIDTH;
    let left = menuAlign === 'right' ? rect.right - panelWidth : rect.left;
    if (left < MENU_MARGIN) left = MENU_MARGIN;
    if (left + panelWidth > window.innerWidth - MENU_MARGIN) {
      left = window.innerWidth - panelWidth - MENU_MARGIN;
    }
    setMenuPos({ top: rect.bottom + MENU_GAP, left });
  }, [menuAlign]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    placeMenu();
  }, [open, placeMenu, value.selectedKeys.length, value.radio]);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };

    window.addEventListener('resize', placeMenu);
    window.addEventListener('scroll', placeMenu, true);
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('resize', placeMenu);
      window.removeEventListener('scroll', placeMenu, true);
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, placeMenu]);

  const handleTriggerClick = () => {
    if (disabled) return;
    setOpen((prev) => !prev);
  };

  const moveRadio = (direction: 1 | -1) => {
    const index = RADIOS.findIndex((radio) => radio.id === value.radio);
    const next = RADIOS[(index + direction + RADIOS.length) % RADIOS.length];
    onChange(selectStatusFilterRadio(next.id));
    radioRefs.current[next.id]?.focus();
  };

  const menu =
    open && menuPos
      ? createPortal(
          <div
            ref={menuRef}
            role="menu"
            data-menu-align={menuAlign}
            aria-label="Progress status filter"
            style={{
              position: 'fixed',
              top: menuPos.top,
              left: menuPos.left,
              zIndex: 10000,
            }}
            className={`${toolbarDropdownPanelPortaled} min-w-[18rem] w-[18rem] max-w-[18rem] py-0`}
          >
            <div
              role="radiogroup"
              aria-label="Status bucket"
              className="flex items-center gap-0.5 px-2 pt-2 pb-1.5"
            >
              {RADIOS.map((radio) => {
                const checked = value.radio === radio.id;
                return (
                  <button
                    key={radio.id}
                    ref={(node) => {
                      radioRefs.current[radio.id] = node;
                    }}
                    type="button"
                    role="radio"
                    aria-checked={checked}
                    tabIndex={checked ? 0 : -1}
                    onClick={() => onChange(selectStatusFilterRadio(radio.id))}
                    onKeyDown={(event) => {
                      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                        event.preventDefault();
                        moveRadio(1);
                      }
                      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                        event.preventDefault();
                        moveRadio(-1);
                      }
                    }}
                    className={`inline-flex h-8 items-center gap-1.5 rounded-full px-2.5 text-sm font-medium ${
                      checked
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-neutral-600 hover:bg-neutral-50 hover:text-primary-600'
                    }`}
                  >
                    <span
                      aria-hidden
                      className={`flex h-3.5 w-3.5 items-center justify-center rounded-full border ${
                        checked
                          ? 'border-primary-500 bg-primary-500'
                          : 'border-primary-500/40 bg-white'
                      }`}
                    >
                      {checked ? (
                        <span className="h-1.5 w-1.5 rounded-full bg-white" />
                      ) : null}
                    </span>
                    {radio.label}
                  </button>
                );
              })}
            </div>

            <div className="max-h-[18rem] overflow-y-auto py-1">
              <button
                type="button"
                role="menuitemcheckbox"
                aria-checked={
                  allCheck === 'indeterminate' ? 'mixed' : allCheck === 'checked'
                }
                onClick={() => onChange(toggleAllStatuses(value))}
                className={`flex w-full items-center justify-between px-3 py-2 text-sm font-semibold ${
                  allCheck === 'unchecked'
                    ? 'text-neutral-800 hover:bg-neutral-50'
                    : 'bg-primary-50/70 text-primary-700'
                }`}
              >
                <span>All statuses</span>
                <StatusCheckMark state={allCheck} />
              </button>

              <p className="px-3 pt-2 pb-0.5 text-xs font-semibold text-neutral-500">
                Active
              </p>
              {ACTIVE_STATUS_OPTIONS.map((option) => {
                const checked = selected.has(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="menuitemcheckbox"
                    aria-checked={checked}
                    onClick={() => onChange(toggleStatusFilterValue(value, option.value))}
                    className={`flex w-full items-center justify-between px-3 py-1.5 text-sm ${
                      checked
                        ? 'bg-primary-50/70 font-medium text-primary-700'
                        : 'font-medium text-neutral-800 hover:bg-neutral-50'
                    }`}
                  >
                    <span>{option.label}</span>
                    <StatusCheckMark state={checked ? 'checked' : 'unchecked'} />
                  </button>
                );
              })}

              <p className="px-3 pt-2 pb-0.5 text-xs font-semibold text-neutral-500">
                Inactive
              </p>
              {INACTIVE_STATUS_OPTIONS.map((option) => {
                const checked = selected.has(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="menuitemcheckbox"
                    aria-checked={checked}
                    onClick={() => onChange(toggleStatusFilterValue(value, option.value))}
                    className={`flex w-full items-center justify-between px-3 py-1.5 text-sm ${
                      checked
                        ? 'bg-primary-50/70 font-medium text-primary-700'
                        : 'font-medium text-neutral-800 hover:bg-neutral-50'
                    }`}
                  >
                    <span>{option.label}</span>
                    <StatusCheckMark state={checked ? 'checked' : 'unchecked'} />
                  </button>
                );
              })}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div ref={rootRef} className={`relative z-20 ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        onClick={handleTriggerClick}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="menu"
        title={title}
        disabled={disabled}
        className={`${toolbarStandaloneBase} justify-between gap-1.5 ${
          isActive ? toolbarStandaloneActive : toolbarStandaloneInactive
        } ${disabled ? 'opacity-70 cursor-not-allowed' : ''} ${triggerClassName}`}
      >
        <span className="truncate font-semibold">{triggerLabel}</span>
        <ChevronDownIcon
          className={`w-3.5 h-3.5 shrink-0 text-primary-400 transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          }`}
          aria-hidden
        />
      </button>
      {menu}
    </div>
  );
};

export default TargetInvestorStatusFilter;
